'use client';

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { MdCheckCircle } from 'react-icons/md';

import { cn } from '@/lib/styleUtils';

/**
 * Lets a puzzle render its own instruction line while the shell still owns the solved state.
 *
 * The alternative — hoisting every prompt into the shell — does not work: most prompts name a
 * value only the puzzle knows ("Tap spot P37", "Tap the green car"), and that value is rerolled
 * inside the puzzle on every new challenge.
 */
const PuzzleSolvedContext = createContext(false);

export const PuzzleSolvedProvider = PuzzleSolvedContext.Provider;

/**
 * The instruction line, swapped for a "Verified" confirmation once the shell marks the challenge
 * solved. Every puzzle renders exactly one of these as its first child.
 */
export const PuzzlePrompt = ({ children }: { children: ReactNode }) => {
  const t = useTranslations('Captcha');
  const solved = useContext(PuzzleSolvedContext);

  if (solved) {
    return (
      <p
        className="tw:flex tw:items-center tw:gap-micro tw:text-14 tw:font-semibold tw:text-success"
        data-cy="captcha-puzzle-verified"
      >
        <MdCheckCircle size={18} aria-hidden="true" />
        {t('verified')}
      </p>
    );
  }

  return (
    <p className="tw:text-14 tw:font-regular tw:text-gray-dark" data-cy="captcha-puzzle-prompt">
      {children}
    </p>
  );
};

PuzzlePrompt.displayName = 'PuzzlePrompt';

interface PuzzleSceneProps {
  /** Scene height in px. Each puzzle sizes its own playfield; width is always 312. */
  height: number;
  children: ReactNode;
  className?: string;
  'data-cy'?: string;
}

/**
 * The neutral playfield every puzzle draws into.
 *
 * Fixed 312px wide because the bay/gate geometry in `puzzle-utils` is expressed in absolute
 * pixels against it — a responsive width would move the hit targets out from under the answers.
 * The scene is centred instead of stretched, which also keeps the challenge identically sized on
 * a phone and a desktop dialog.
 */
export const PuzzleScene = ({
  height,
  children,
  className,
  'data-cy': dataCy = 'captcha-puzzle-scene',
}: PuzzleSceneProps) => (
  <div
    className={cn(
      'tw:relative tw:mx-auto tw:w-[312px] tw:shrink-0 tw:overflow-hidden',
      'tw:rounded-8 tw:bg-gray-lighter',
      className
    )}
    style={{ height }}
    data-cy={dataCy}
  >
    {children}
  </div>
);

PuzzleScene.displayName = 'PuzzleScene';

interface PuzzleGridProps {
  columns: number;
  children: ReactNode;
  'data-cy'?: string;
}

/** Tap-target grid for the choose-one puzzles (2h, 2j). */
export const PuzzleGrid = ({
  columns,
  children,
  'data-cy': dataCy = 'captcha-puzzle-grid',
}: PuzzleGridProps) => (
  <div
    className="tw:mx-auto tw:grid tw:w-[312px] tw:gap-micro"
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    data-cy={dataCy}
  >
    {children}
  </div>
);

PuzzleGrid.displayName = 'PuzzleGrid';
